import React from 'react';
import { observer } from 'mobx-react';
import { CapacityPlannerScreen } from '../../screen/CapacityPlanner/Screen';
import { projectColor, userColor, formatDate, isToday, isWeekend, getDayOfWeek, daysBetween, addDays, toDate } from './types';
import { PriorityQueuePanel } from './PriorityQueuePanel';

interface Props { planner: CapacityPlannerScreen; }

@observer
export class DetailView extends React.Component<Props> {
    render() {
        const p = this.props.planner;
        if (!p.detailViewOpen || !p.detailProjectId) return null;
        const proj = p.projects.find(pr => pr.id === p.detailProjectId);
        if (!proj) return null;
        const pStart = p.getProjectStartDate(proj);
        const pEnd = p.getProjectEndDate(proj);
        const deadlineStatus = p.getDeadlineStatus(proj);
        const allocs = p.getProjectAllocations(proj);
        const allocatedDevs = allocs.map(a => p.users.find(u => u.id === (a as any).user?.id)).filter(Boolean) as any[];
        const ms = [...p.getProjectMilestones(proj), ...p.getProjectPlanningMilestones(proj)];
        const totalDays = Math.max(daysBetween(pStart, pEnd) + 14, 28);
        const detailDates: Date[] = [];
        for (let i = -7; i < totalDays; i++) detailDates.push(addDays(pStart, i));
        const timelineStart = detailDates[0];
        const dw = p.detailDayWidth;
        return (
            <div className="project-detail-view" data-test-cp-detail-view>
                <header className="project-detail-header">
                    <button className="back-btn" onClick={p.closeDetailView}>← <span>Back to Overview</span></button>
                    <div className="project-title">
                        <div className="project-color-dot" style={{ background: projectColor(proj) }} />
                        <h2>{proj.name}</h2>
                    </div>
                    <div className="project-dates">{formatDate(pStart)} - {formatDate(pEnd)}</div>
                    {deadlineStatus && <div className={'deadline-badge ' + deadlineStatus.status}>{deadlineStatus.message}</div>}
                </header>
                <div className="project-detail-body">
                    <aside className="feature-sidebar">
                        {p.viewingMilestoneId && (p.planningMilestones as any[]).some(m => m.id === p.viewingMilestoneId) ? (
                            <PriorityQueuePanel planner={p} />
                        ) : (
                        <>
                        <div className="feature-sidebar-header">
                            <h3>Features</h3>
                            <button className="btn btn-sm btn-primary" onClick={p.addDetailMilestone}>+ Add</button>
                        </div>
                        <div className="feature-list">
                            {ms.map(m => {
                                const mAny = m as any;
                                const mTickets = p.getMilestoneTickets(mAny);
                                const mHours = mTickets.reduce((s, t) => s + p.getTicketEstimate(t), 0);
                                const doneCount = mTickets.filter(t => p.getTicketStatus(t) === 'done').length;
                                const mDeadlineStatus = p.getMilestoneDeadlineStatus(mAny);
                                const typeIcon = { sprint: '🏃', feature: '⭐', milestone: '🎯', phase: '📋' }[(mAny as any).type || 'milestone'] || '📁';
                                const expanded = p.expandedMilestones.has(m.id as number);
                                return (
                                    <div key={m.id} className={'milestone-group' + (expanded ? ' expanded' : ' collapsed')}>
                                        <div className="milestone-header" onClick={() => p.toggleMilestone(m.id as number)}>
                                            <div className="milestone-info">
                                                <span className="milestone-icon">{typeIcon}</span>
                                                <span className="milestone-name">{m.name}</span>
                                                <span className="milestone-progress">{doneCount}/{mTickets.length}</span>
                                            </div>
                                            <div className="milestone-meta">
                                                <span className="milestone-hours">{mHours}h</span>
                                                {mAny.dueAt && <span className={'milestone-deadline' + (mDeadlineStatus?.status ? ' ' + mDeadlineStatus.status : '')}>{formatDate(mAny.dueAt)}</span>}
                                            </div>
                                        </div>
                                        <div className="milestone-tickets" onDragOver={(e) => e.preventDefault()} onDrop={(e) => p.onMilestoneDrop(e, m.id as number)}>
                                            {mTickets.map(t => {
                                                const tAny = t as any;
                                                const assignee = p.getTicketAssignee(tAny);
                                                const isScheduled = p.getTicketScheduledStart(tAny) && p.getTicketScheduledEnd(tAny);
                                                const status = p.getTicketStatus(tAny);
                                                return (
                                                    <div key={t.id} className={'ticket-card' + (status === 'done' ? ' done' : '') + (isScheduled ? ' scheduled' : ' unscheduled') + (p.selectedFeatureId === t.id ? ' selected' : '')}
                                                        onClick={() => p.selectFeature(t.id as number)}>
                                                        <div className={'ticket-status-dot ' + status} />
                                                        <div className="ticket-info">
                                                            <div className="ticket-name">{tAny.title || tAny.fullName}</div>
                                                            <div className="ticket-meta">
                                                                {assignee ? (
                                                                    <div className="ticket-assignee">
                                                                        <div className="assignee-dot" style={{ background: userColor(assignee) }}>{(assignee.fullName || '?').charAt(0)}</div>
                                                                        {assignee.fullName}
                                                                    </div>
                                                                ) : <span className="unassigned">Unassigned</span>}
                                                                <span className="ticket-estimate">{p.getTicketEstimate(tAny)}h</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <button className="btn-add-ticket" onClick={() => p.addTicket(m.id as number)}>+ Add Ticket</button>
                                        </div>
                                    </div>
                                );
                            })}
                            <button className="btn btn-sm btn-secondary" style={{ marginTop: '1rem', width: '100%' }} onClick={p.addDetailMilestone}>+ Add Milestone</button>
                        </div>
                        <div className="unscheduled-drop-zone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => p.onUnscheduledDrop(e)}>
                            <h4>Unscheduled</h4>
                            <div className="unscheduled-features">
                                {p.getUnscheduledTickets(proj).map(t => {
                                    const tAny = t as any;
                                    const assignee = p.getTicketAssignee(tAny);
                                    return (
                                        <div key={t.id} className="unscheduled-chip" draggable
                                            onDragStart={() => { (window as any).__cpDragTicketId = t.id; }}>
                                            {assignee && <div className="assignee-dot" style={{ background: userColor(assignee) }}>{(assignee.fullName || '?').charAt(0)}</div>}
                                            <span>{tAny.title || tAny.fullName}</span>
                                            <span className="chip-hours">{p.getTicketEstimate(tAny)}h</span>
                                        </div>
                                    );
                                })}
                                {p.getUnscheduledTickets(proj).length === 0 && <span style={{ color: 'var(--cp-text-secondary)', fontSize: '0.75rem' }}>All tickets scheduled</span>}
                            </div>
                        </div>
                        </>
                        )}
                    </aside>
                    <div className="feature-timeline-container">
                        <div className="feature-timeline-header">
                            <div className="timeline-controls">
                                <button className="btn btn-sm" onClick={p.zoomOutDetail}>-</button>
                                <span className="zoom-level">{p.detailZoomLabel}</span>
                                <button className="btn btn-sm" onClick={p.zoomInDetail}>+</button>
                                <button className="btn btn-sm" onClick={() => p.scrollToTodayDetail(timelineStart)}>Today</button>
                            </div>
                            <div className="timeline-info">
                                {p.viewingMilestoneId ? (
                                    <>
                                        <button className="back-to-milestones" onClick={p.closeMilestoneDetail}>← Back to Milestones</button>
                                        <span className="viewing-milestone">Viewing: {ms.find(m => m.id === p.viewingMilestoneId)?.name}</span>
                                    </>
                                ) : 'Double-click milestone to view tickets'}
                            </div>
                        </div>
                        <div className="availability-legend">
                            <div className="availability-legend-item"><div className="legend-color available" /><span>Allocated</span></div>
                            <div className="availability-legend-item"><div className="legend-color partial" /><span>Part-time</span></div>
                            <div className="availability-legend-item"><div className="legend-color not-allocated" /><span>Not on project</span></div>
                            <div className="availability-legend-item"><div className="legend-color unavailable" /><span>Not working</span></div>
                            <div className="availability-legend-item"><div className="legend-color holiday" /><span>Holiday</span></div>
                        </div>
                        <div className="feature-timeline-wrapper">
                            <div className="feature-timeline-labels">
                                {p.viewingMilestoneId ? this.renderDevTimelineLabels(proj, allocatedDevs) : this.renderMilestoneTimelineLabels(ms)}
                            </div>
                            <div className="feature-timeline-scroll" id="feature-timeline-scroll">
                                {this.renderTimelineDates(detailDates, dw)}
                                {this.renderTimelineGrid(detailDates, dw)}
                                <div className="feature-timeline-content" style={{ width: detailDates.length * dw }}>
                                    {p.viewingMilestoneId
                                        ? this.renderMilestoneTicketTimeline(proj, allocatedDevs, detailDates, timelineStart)
                                        : this.renderMilestoneOverviewTimeline(proj, ms, detailDates, timelineStart)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderTimelineDates(dates: Date[], width: number) {
        return (
            <div className="timeline-dates">
                {dates.map((date, i) => (
                    <div key={i} className={'timeline-date' + (isToday(date) ? ' today' : '') + (isWeekend(date) ? ' weekend' : '')} style={{ width }}>
                        <span className="month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="day">{date.getDate()}</span>
                    </div>
                ))}
            </div>
        );
    }

    renderTimelineGrid(dates: Date[], width: number) {
        return (
            <div className="timeline-grid">
                {dates.map((date, i) => (
                    <div key={i} className={'timeline-grid-line' + (isToday(date) ? ' today' : '')} style={{ left: i * width }} />
                ))}
            </div>
        );
    }

    renderMilestoneTimelineLabels(ms: any[]) {
        const p = this.props.planner;
        return ms.map(m => {
            const mAny = m as any;
            const mTickets = p.getMilestoneTickets(mAny);
            const doneCount = mTickets.filter(t => p.getTicketStatus(t) === 'done').length;
            const totalHours = mTickets.reduce((s, t) => s + p.getTicketEstimate(t), 0);
            const deadlineStatus = p.getMilestoneDeadlineStatus(mAny);
            const typeIcon = { sprint: '🏃', feature: '⭐', milestone: '🎯', phase: '📋' }[(mAny as any).type || 'milestone'] || '📁';
            return (
                <div key={m.id} className="milestone-timeline-label" onClick={() => p.selectMilestone(m.id as number)} onDoubleClick={() => p.openMilestoneDetail(m.id as number)}>
                    <span className="milestone-type-icon">{typeIcon}</span>
                    <div className="milestone-label-info">
                        <div className="milestone-label-name">{m.name}</div>
                        <div className="milestone-label-meta">{doneCount}/{mTickets.length} tickets · {totalHours}h{deadlineStatus?.status === 'exceeded' ? ' · ⚠️' : ''}</div>
                    </div>
                </div>
            );
        });
    }

    renderMilestoneOverviewTimeline(proj: any, ms: any[], dates: Date[], timelineStart: Date) {
        const p = this.props.planner;
        const dw = p.detailDayWidth;
        return ms.map(m => {
            const mAny = m as any;
            const mDates = p.getMilestoneDates(mAny);
            const deadlineStatus = p.getMilestoneDeadlineStatus(mAny);
            const mTickets = p.getMilestoneTickets(mAny);
            const assigneeIds = [...new Set(mTickets.map(t => p.getTicketAssignee(t)?.id).filter(Boolean))] as number[];
            const assigneeDots = assigneeIds.slice(0, 4).map(devId => {
                const dev = p.users.find(u => u.id === devId) as any;
                if (!dev) return null;
                return <div key={devId} className="assignee-dot" style={{ background: userColor(dev) }} title={dev.fullName}>{(dev.fullName || '?').charAt(0)}</div>;
            });
            if (!mDates) {
                return (
                    <div key={m.id} className="milestone-timeline-row">
                        <div className="milestone-unscheduled" data-milestone-id={m.id} onDoubleClick={() => p.openMilestoneDetail(m.id as number)}>
                            <span>No tickets scheduled - double-click to schedule</span>
                        </div>
                    </div>
                );
            }
            const startOffset = daysBetween(timelineStart, mDates.start);
            const duration = daysBetween(mDates.start, mDates.end) + 1;
            const left = startOffset * dw;
            const width = Math.max(duration * dw, 80);
            const completedCount = mTickets.filter(t => p.getTicketStatus(t) === 'done').length;
            const totalCount = mTickets.length;
            const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
            let deadlineMarker = null;
            // [T54157] operation milestones use `dueAt`, planning milestones use
            // `deadline` (a moment via Casts.date) — normalize both.
            const deadlineDate = toDate(mAny.dueAt ?? mAny.deadline);
            if (deadlineDate) {
                const deadlineOffset = daysBetween(timelineStart, deadlineDate);
                const deadlineLeft = deadlineOffset * dw;
                deadlineMarker = <div className={'milestone-deadline-marker' + (deadlineStatus?.status ? ' ' + deadlineStatus.status : '')} style={{ left: deadlineLeft }} title={`Deadline: ${formatDate(deadlineDate)}`} />;
            }
            return (
                <div key={m.id} className="milestone-timeline-row">
                    <div className={'milestone-bar' + (deadlineStatus?.status === 'exceeded' ? ' exceeded' : '') + (p.selectedMilestoneId === m.id ? ' selected' : '')}
                        style={{ left, width, background: projectColor(proj) }}
                        onClick={() => p.selectMilestone(m.id as number)} onDoubleClick={() => p.openMilestoneDetail(m.id as number)}>
                        <div className="milestone-bar-progress" style={{ width: `${progress}%` }} />
                        <div className="milestone-bar-content">
                            <span className="milestone-bar-name">{m.name}</span>
                            <span className="milestone-bar-info">{completedCount}/{totalCount}</span>
                        </div>
                        <div className="milestone-assignees">{assigneeDots}</div>
                    </div>
                    {deadlineMarker}
                </div>
            );
        });
    }

    renderDevTimelineLabels(proj: any, allocatedDevs: any[]) {
        const p = this.props.planner;
        const milestone = (p.planningMilestones as any[]).find(m => m.id === p.viewingMilestoneId) || p.milestones.find(m => m.id === p.viewingMilestoneId);
        if (!milestone) return null;
        const mTickets = p.getMilestonePlanningTickets(milestone);
        const milestoneDevIds = [...new Set(mTickets.map(t => p.getTicketAssignee(t)?.id).filter(Boolean))] as number[];
        const relevantDevs = allocatedDevs.filter(d => milestoneDevIds.includes(d.id));
        return relevantDevs.map(dev => {
            const weeklyHours = p.getDevWeeklyHours(dev);
            const isPartTime = p.isPartTime(dev);
            const a = p.getAllocationForUser(proj, dev.id);
            const days = a ? p.getAllocatedDays(a) : [];
            return (
                <div key={dev.id} className="dev-timeline-label" onClick={() => p.openAllocationEditor(proj.id, dev.id)} style={{ cursor: 'pointer' }} title="Click to edit allocation">
                    <div className="dev-avatar" style={{ background: userColor(dev) }}>{(dev.fullName || '?').charAt(0)}</div>
                    <div className="dev-details">
                        <div className="dev-name">{dev.fullName}</div>
                        <div className={'dev-hours' + (isPartTime ? ' part-time' : '')}>
                            {weeklyHours}h/week · {days.length > 0 ? days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ') : 'Not allocated'}
                        </div>
                    </div>
                </div>
            );
        });
    }

    renderMilestoneTicketTimeline(proj: any, allocatedDevs: any[], dates: Date[], timelineStart: Date) {
        const p = this.props.planner;
        const dw = p.detailDayWidth;
        const milestone = (p.planningMilestones as any[]).find(m => m.id === p.viewingMilestoneId) || p.milestones.find(m => m.id === p.viewingMilestoneId);
        if (!milestone) return null;
        const mTickets = p.getMilestonePlanningTickets(milestone);
        const milestoneDevIds = [...new Set(mTickets.map(t => p.getTicketAssignee(t)?.id).filter(Boolean))] as number[];
        const relevantDevs = allocatedDevs.filter(d => milestoneDevIds.includes(d.id));
        return relevantDevs.map(dev => {
            const availabilityCells = dates.map((date, i) => {
                const avail = p.getProjectAvailabilityForDate(dev, proj, date);
                return <div key={i} className={'availability-cell ' + avail} style={{ width: dw }} title={getDayOfWeek(date)} />;
            });
            const devTickets = mTickets.filter(t => p.getTicketAssignee(t)?.id === dev.id && p.getTicketScheduledStart(t) && p.getTicketScheduledEnd(t));
            // [T54157] Lane assignment: tickets of the same dev that overlap in
            // time (e.g. one ends Tuesday, the next starts Tuesday because of a
            // partial-day split) must render in separate lanes instead of
            // stacking on top of each other.
            const lanes: { start: Date; end: Date }[][] = [];
            const laneByTicket = new Map<any, number>();
            for (const t of devTickets) {
                const s = new Date(p.getTicketScheduledStart(t) as Date);
                const e = new Date(p.getTicketScheduledEnd(t) as Date);
                let laneIdx = lanes.findIndex(l => l.every(seg => e < seg.start || s > seg.end));
                if (laneIdx === -1) {
                    laneIdx = lanes.length;
                    lanes.push([]);
                }
                lanes[laneIdx].push({ start: s, end: e });
                laneByTicket.set(t, laneIdx);
            }
            const laneCount = Math.max(lanes.length, 1);
            const laneHeight = 34;
            const rowContentHeight = 24 + laneCount * laneHeight;
            const ticketBars = devTickets.map(t => {
                const tAny = t as any;
                const status = p.getTicketStatus(tAny);
                const statusClass = (() => { if (status === 'done') return 'completed'; if (status === 'in-progress' || status === 'in_progress') return 'in-progress'; return ''; })();
                const start = p.getTicketScheduledStart(tAny) as Date;
                const end = p.getTicketScheduledEnd(tAny) as Date;
                const segments: { start: Date; days: number }[] = [];
                let current = new Date(start);
                let segStart: Date | null = null;
                let segDays = 0;
                while (current <= end) {
                    const avail = p.getProjectAvailabilityForDate(dev, proj, current);
                    const isWorking = avail === 'available' || avail === 'partial';
                    if (isWorking) { if (!segStart) { segStart = new Date(current); segDays = 1; } else segDays++; }
                    else { if (segStart) { segments.push({ start: segStart, days: segDays }); segStart = null; segDays = 0; } }
                    current = addDays(current, 1);
                }
                if (segStart) segments.push({ start: segStart, days: segDays });
                const lane = laneByTicket.get(t) || 0;
                return segments.map((seg, idx) => {
                    const startOffset = daysBetween(timelineStart, seg.start);
                    const left = startOffset * dw;
                    const width = seg.days * dw;
                    const isFirst = idx === 0;
                    const isLast = idx === segments.length - 1;
                    return (
                        <div key={`${t.id}-${idx}`} className={'feature-bar' + (statusClass ? ' ' + statusClass : '') + (p.selectedFeatureId === t.id ? ' selected' : '') + (!isFirst ? ' feature-segment' : '')}
                            style={{ left, width, top: 10 + lane * laneHeight, height: Math.min(laneHeight - 4, 30), background: projectColor(proj) }}
                            onClick={() => p.selectFeature(t.id as number)} onDoubleClick={() => p.editTicketInline(t)}>
                            {isFirst && (
                                <>
                                    <div className="feature-bar-content">
                                        <span className="feature-bar-name">{tAny.title || tAny.fullName}</span>
                                        <span className="feature-bar-hours">{p.getTicketEstimate(tAny)}h</span>
                                    </div>
                                    <div className="schedule-hint">{formatDate(start)} - {formatDate(end)}</div>
                                    <div className="resize-handle left" onMouseDown={(e) => p.startTicketResize(e, t, 'left')} />
                                </>
                            )}
                            {isLast && <div className="resize-handle right" onMouseDown={(e) => p.startTicketResize(e, t, 'right')} />}
                        </div>
                    );
                });
            });
            return (
                <div key={dev.id} className="dev-timeline-row" data-test-cp-dev-row={dev.id} style={{ height: rowContentHeight }}>
                    <div className="availability-row">{availabilityCells}</div>
                    {ticketBars}
                </div>
            );
        });
    }
}
